import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  Camera,
  CameraResultType,
  CameraSource,
  type CameraPluginPermissions,
  type ImageOptions,
  type Photo,
} from '@capacitor/camera';
import { TranslateService } from '@ngx-translate/core';

export interface SelectedCameraPhoto {
  file: File;
  previewDataUrl: string;
  mimeType: string;
}

export type CameraSelectionOutcome =
  | { kind: 'success'; photo: SelectedCameraPhoto }
  | { kind: 'canceled' }
  | { kind: 'unsupportedEnvironment' }
  | { kind: 'error'; error: unknown };

const WEB_ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
] as const;

@Injectable({ providedIn: 'root' })
export class CameraService {
  readonly #translate = inject(TranslateService);

  takePhoto(): Promise<CameraSelectionOutcome> {
    return this.#pickPhoto(CameraSource.Camera);
  }

  pickFromGallery(): Promise<CameraSelectionOutcome> {
    return this.#pickPhoto(CameraSource.Photos);
  }

  /**
   * Нативный выбор: камера или галерея (на iOS — action sheet, на Android — системный диалог).
   */
  selectPhoto(): Promise<CameraSelectionOutcome> {
    return this.#pickPhoto(CameraSource.Prompt);
  }

  async #pickPhoto(source: CameraSource): Promise<CameraSelectionOutcome> {
    const platform = Capacitor.getPlatform();
    if (platform === 'web') {
      return this.#pickWebFile();
    }
    if (platform !== 'ios' && platform !== 'android') {
      return { kind: 'unsupportedEnvironment' };
    }

    try {
      await this.#requestPermissions(source);
      const options = this.#buildImageOptions(source);
      const photo = await Camera.getPhoto(options);
      const selected = await this.#photoToSelected(photo);
      return { kind: 'success', photo: selected };
    } catch (error) {
      if (this.#isUserCanceled(error)) {
        return { kind: 'canceled' };
      }
      return { kind: 'error', error };
    }
  }

  async #requestPermissions(source: CameraSource): Promise<void> {
    const permissions: CameraPluginPermissions['permissions'] = [];
    if (source === CameraSource.Camera) {
      permissions.push('camera');
    } else if (source === CameraSource.Photos) {
      permissions.push('photos');
    } else {
      permissions.push('camera', 'photos');
    }
    await Camera.requestPermissions({ permissions });
  }

  #buildImageOptions(source: CameraSource): ImageOptions {
    const base: ImageOptions = {
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      correctOrientation: true,
      source,
    };

    if (source !== CameraSource.Prompt) {
      return base;
    }

    return {
      ...base,
      promptLabelHeader: this.#translate.instant(
        'admin.employees.photo.promptTitle',
      ),
      promptLabelCancel: this.#translate.instant('common.actions.cancel'),
      promptLabelPhoto: this.#translate.instant(
        'admin.employees.photo.gallery',
      ),
      promptLabelPicture: this.#translate.instant(
        'admin.employees.photo.camera',
      ),
    };
  }

  async #photoToSelected(photo: Photo): Promise<SelectedCameraPhoto> {
    const dataUrl = photo.dataUrl;
    if (!dataUrl) {
      throw new Error('Camera returned no dataUrl');
    }
    const mimeType = this.#formatToMimeType(photo.format);
    const ext = photo.format === 'jpeg' ? 'jpg' : photo.format;
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const type = blob.type || mimeType;
    const file = new File([blob], `photo.${ext}`, { type });
    return {
      file,
      previewDataUrl: dataUrl,
      mimeType: type,
    };
  }

  #formatToMimeType(format: string): string {
    if (format === 'jpeg') {
      return 'image/jpeg';
    }
    return `image/${format}`;
  }

  #isUserCanceled(error: unknown): boolean {
    const msg = (
      error instanceof Error ? error.message : String(error)
    ).toLowerCase();
    return (
      msg.includes('cancel') ||
      msg.includes('closed') ||
      msg.includes('dismiss') ||
      msg.includes('user cancelled')
    );
  }

  /**
   * Браузер / dev: выбор файла через системный диалог (без Capacitor Camera).
   */
  #pickWebFile(): Promise<CameraSelectionOutcome> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = WEB_ALLOWED_IMAGE_TYPES.join(',');
      let settled = false;
      const finish = (outcome: CameraSelectionOutcome): void => {
        if (settled) {
          return;
        }
        settled = true;
        window.removeEventListener('focus', onWindowFocus);
        resolve(outcome);
      };
      const onWindowFocus = (): void => {
        setTimeout(() => {
          if (!input.files?.length) {
            finish({ kind: 'canceled' });
          }
        }, 300);
      };
      input.addEventListener('change', () => {
        const file = input.files?.[0];
        if (!file) {
          finish({ kind: 'canceled' });
          return;
        }
        if (
          !WEB_ALLOWED_IMAGE_TYPES.includes(
            file.type as (typeof WEB_ALLOWED_IMAGE_TYPES)[number],
          )
        ) {
          finish({
            kind: 'error',
            error: new Error(
              `Unsupported image type: ${file.type || '(empty)'}`,
            ),
          });
          return;
        }
        const reader = new FileReader();
        reader.onload = (): void => {
          const previewDataUrl = reader.result;
          if (typeof previewDataUrl !== 'string') {
            finish({
              kind: 'error',
              error: new Error('Failed to read image preview'),
            });
            return;
          }
          finish({
            kind: 'success',
            photo: {
              file,
              previewDataUrl,
              mimeType: file.type,
            },
          });
        };
        reader.onerror = (): void =>
          finish({
            kind: 'error',
            error: reader.error ?? new Error('FileReader failed'),
          });
        reader.readAsDataURL(file);
      });
      window.addEventListener('focus', onWindowFocus);
      input.click();
    });
  }
}
